import React, { useState } from 'react';
import { Combobox, InputBase, useCombobox, Loader } from '@mantine/core';

export interface AfaComboboxProps {
  /** Datos para el combobox - array de strings o objetos con value/label */
  data: string[] | { value: string; label: string }[];

  /** Valor seleccionado actual */
  value?: string;

  /** Callback cuando cambia la selección */
  onChange?: (value: string) => void;

  /** Placeholder del input */
  placeholder?: string;

  /** Label del campo */
  label?: string;

  /** Descripción adicional */
  description?: string;

  /** Mensaje de error */
  error?: string;

  /** Si es requerido */
  required?: boolean;

  /** Si está deshabilitado */
  disabled?: boolean;

  /** Tamaño del input */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /** Radio de bordes */
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /** Filtrable por texto */
  searchable?: boolean;

  /** Texto cuando no hay resultados */
  nothingFound?: string;

  /** Nombre del campo para formularios */
  name?: string;

  /** ID del campo */
  id?: string;

  /** Props adicionales para el InputBase */
  inputProps?: any;

  /** Estilos personalizados */
  styles?: any;

  /** Clase CSS */
  className?: string;

  /** Si está cargando datos (muestra spinner en lugar del chevron) */
  loading?: boolean;
}

/**
 * Wrapper del Combobox de Mantine con defaults de AFA y estilos del tema
 * 
 * Ejemplos de uso:
 * 
 * // Básico con array de strings
 * <AfaCombobox
 *   data={['React', 'Angular', 'Vue', 'Svelte']}
 *   placeholder="Selecciona framework"
 *   label="Framework"
 * />
 * 
 * // Con objetos value/label
 * <AfaCombobox
 *   data={[
 *     { value: 'react', label: 'React' },
 *     { value: 'angular', label: 'Angular' }
 *   ]}
 *   searchable
 *   placeholder="Buscar..."
 * />
 * 
 * // Controlado
 * const [value, setValue] = useState('');
 * <AfaCombobox
 *   data={options}
 *   value={value}
 *   onChange={setValue}
 * />
 */
export function AfaCombobox({
  data,
  value,
  onChange,
  placeholder = 'Seleccioni opció',
  label,
  description,
  error,
  required = false,
  disabled = false,
  size = 'sm',
  radius = 'md',
  searchable = true,
  nothingFound = 'No s\'han trobat resultats',
  name,
  id,
  inputProps,
  styles,
  className,
  loading = false,
}: AfaComboboxProps) {
  const [search, setSearch] = useState('');
  const [internalValue, setInternalValue] = useState(value || '');

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch(''); // Limpiar search para mostrar el valor seleccionado
    },
    onDropdownOpen: () => {
      if (searchable) {
        combobox.focusSearchInput();
      }
    },
  });

  // Normalizar datos a formato { value, label }
  const normalizedData = data.map(item =>
    typeof item === 'string'
      ? { value: item, label: item }
      : item
  );

  // Filtrar datos si es searchable
  const filteredData = searchable
    ? normalizedData.filter(item =>
      item.label.toLowerCase().includes(search.toLowerCase().trim())
    )
    : normalizedData;

  // Encontrar el item seleccionado para mostrar su label
  const currentValue = value !== undefined ? value : internalValue;
  const selectedItem = normalizedData.find(item => item.value === currentValue);

  // Mostrar el valor seleccionado o el texto de búsqueda
  const displayValue = selectedItem && !search ? selectedItem.label : search;

  // Manejar selección
  const handleSelect = (selectedValue: string) => {
    const isControlled = value !== undefined;

    if (isControlled) {
      onChange?.(selectedValue);
    } else {
      setInternalValue(selectedValue);
      onChange?.(selectedValue);
    }

    setSearch('');
    combobox.closeDropdown();
  };

  // Generar opciones del dropdown
  const options = filteredData.map((item) => (
    <Combobox.Option value={item.value} key={item.value}>
      {item.label}
    </Combobox.Option>
  ));

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={handleSelect}
      styles={{
        dropdown: {
          borderColor: 'var(--mantine-color-brand-3)',
        },
        option: {
          // Remover selectores problemáticos - Mantine maneja esto internamente
          '&:hover': {
            backgroundColor: 'var(--mantine-color-brand-1)',
          },
        },
        ...styles,
      }}

    >
      <Combobox.Target>
        <InputBase
          rightSection={loading ? <Loader size="xs" /> : <Combobox.Chevron />}
          value={displayValue}
          onChange={(event) => {
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
            setSearch(event.currentTarget.value);
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => {
            combobox.closeDropdown();
            setSearch('');
          }}
          pointer={!searchable}
          readOnly={!searchable}
          placeholder={loading ? "Carregant..." : placeholder}
          label={label}
          description={description}
          error={error}
          required={required}
          disabled={disabled}
          size={size}
          radius={radius}
          name={name}
          id={id}
          styles={{
            input: {
              borderColor: error ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-brand-3)',
              '&:focus': {
                borderColor: 'var(--mantine-color-brand-5)',
                boxShadow: '0 0 0 2px var(--mantine-color-brand-1)',
              },
            },
            label: {
              color: 'var(--mantine-other-black)',
              fontWeight: 500,
            },
          }}
          {...inputProps}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder="Seleccioni opció"
          style={searchable ? {} : { display: 'none' }}
        />
        <Combobox.Options style={{
          maxHeight: '300px',
          overflowY: 'auto'
        }}>
          {options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>{nothingFound}</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}